import { S3Client, HeadObjectCommand, DeleteObjectCommand, ListObjectsV2Command, GetObjectCommand } from '@aws-sdk/client-s3'
import { Upload } from '@aws-sdk/lib-storage'
import fs from 'fs'
import path from 'path'
import { BaseStorageProvider } from './base.provider.js'
import type { FileToUpload, UploadedFile, DownloadedFile, S3Config } from '../types.js'
import { getFileSize, getTimestamp, sanitizePath } from '../utils.js'

/**
 * AWS S3 (and S3-compatible) storage provider
 */
export class S3StorageProvider extends BaseStorageProvider {
  readonly name = 's3' as const
  private client?: S3Client
  private config: S3Config

  constructor(config: S3Config, verbose: boolean = false) {
    super(verbose)
    this.config = config
  }

  async initialize(): Promise<void> {
    try {
      const clientConfig: any = {
        region: this.config.region
      }

      // Add credentials if provided
      if (this.config.accessKeyId && this.config.secretAccessKey) {
        clientConfig.credentials = {
          accessKeyId: this.config.accessKeyId,
          secretAccessKey: this.config.secretAccessKey
        }
      }

      // Add custom endpoint for S3-compatible services
      if (this.config.endpoint) {
        clientConfig.endpoint = this.config.endpoint
        clientConfig.forcePathStyle = true
      }

      this.client = new S3Client(clientConfig)
      this.log(`Initialized S3 Storage: ${this.config.bucket} (${this.config.region})`)
    } catch (error) {
      this.logError('Failed to initialize S3 Storage', error as Error)
      throw error
    }
  }

  async uploadFile(file: FileToUpload): Promise<UploadedFile> {
    if (!this.client) {
      throw new Error('S3 Storage not initialized')
    }

    const localPath = file.path
    const destination = file.destination || path.basename(localPath)
    const remotePath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(destination))
      : sanitizePath(destination)

    const size = await getFileSize(localPath)

    try {
      // Create read stream
      const fileStream = fs.createReadStream(localPath)

      // Upload using multipart upload for better performance
      const upload = new Upload({
        client: this.client,
        params: {
          Bucket: this.config.bucket,
          Key: remotePath,
          Body: fileStream,
          ContentType: file.contentType,
          ACL: this.config.acl as any,
          Metadata: file.metadata
        }
      })

      await upload.done()

      // Construct public URL if ACL is public
      const url = this.config.acl?.includes('public')
        ? this.getPublicUrl(remotePath)
        : undefined

      this.log(`Uploaded ${localPath} → ${remotePath}`)

      return {
        localPath,
        remotePath,
        url,
        size,
        contentType: file.contentType,
        uploadedAt: getTimestamp()
      }
    } catch (error) {
      this.logError(`Failed to upload ${localPath}`, error as Error)
      throw error
    }
  }

  async fileExists(remotePath: string): Promise<boolean> {
    if (!this.client) {
      throw new Error('S3 Storage not initialized')
    }

    const fullPath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(remotePath))
      : sanitizePath(remotePath)

    try {
      await this.client.send(
        new HeadObjectCommand({
          Bucket: this.config.bucket,
          Key: fullPath
        })
      )
      return true
    } catch (error: any) {
      if (error.name === 'NotFound') {
        return false
      }
      this.logError(`Failed to check if file exists: ${remotePath}`, error as Error)
      return false
    }
  }

  async downloadFile(remotePath: string): Promise<DownloadedFile> {
    if (!this.client) {
      throw new Error('S3 Storage not initialized')
    }

    const fullPath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(remotePath))
      : sanitizePath(remotePath)

    try {
      const response = await this.client.send(
        new GetObjectCommand({
          Bucket: this.config.bucket,
          Key: fullPath
        })
      )

      // Convert stream to buffer
      const chunks: Uint8Array[] = []
      for await (const chunk of response.Body as any) {
        chunks.push(chunk)
      }
      const buffer = Buffer.concat(chunks)

      // Get public URL if ACL is public
      const url = this.config.acl?.includes('public')
        ? this.getPublicUrl(fullPath)
        : undefined

      this.log(`Downloaded ${remotePath} (${buffer.length} bytes)`)

      return {
        remotePath,
        buffer,
        url,
        size: buffer.length,
        contentType: response.ContentType,
        downloadedAt: getTimestamp()
      }
    } catch (error) {
      this.logError(`Failed to download ${remotePath}`, error as Error)
      throw error
    }
  }

  async deleteFile(remotePath: string): Promise<void> {
    if (!this.client) {
      throw new Error('S3 Storage not initialized')
    }

    const fullPath = this.config.basePath
      ? path.join(this.config.basePath, sanitizePath(remotePath))
      : sanitizePath(remotePath)

    try {
      await this.client.send(
        new DeleteObjectCommand({
          Bucket: this.config.bucket,
          Key: fullPath
        })
      )
      this.log(`Deleted ${remotePath}`)
    } catch (error) {
      this.logError(`Failed to delete ${remotePath}`, error as Error)
      throw error
    }
  }

  async listFiles(remotePath?: string): Promise<string[]> {
    if (!this.client) {
      throw new Error('S3 Storage not initialized')
    }

    const prefix = remotePath
      ? this.config.basePath
        ? path.join(this.config.basePath, sanitizePath(remotePath))
        : sanitizePath(remotePath)
      : this.config.basePath || ''

    try {
      const response = await this.client.send(
        new ListObjectsV2Command({
          Bucket: this.config.bucket,
          Prefix: prefix
        })
      )

      return response.Contents?.map(obj => obj.Key!) || []
    } catch (error) {
      this.logError('Failed to list files', error as Error)
      throw error
    }
  }

  async cleanup(): Promise<void> {
    if (this.client) {
      this.client.destroy()
      this.log('S3 client cleaned up')
    }
  }

  /**
   * Get public URL for a file
   */
  private getPublicUrl(remotePath: string): string {
    if (this.config.endpoint) {
      // Custom endpoint (S3-compatible service)
      return `${this.config.endpoint}/${this.config.bucket}/${remotePath}`
    } else {
      // Standard AWS S3
      return `https://${this.config.bucket}.s3.${this.config.region}.amazonaws.com/${remotePath}`
    }
  }
}
